import React, { useState } from "react";
import { Package, Calendar } from "lucide-react";
import { PurchaseOrder } from "src/types";
import { CollapsibleSection } from "src/components/ui/collapsible";
import { DateInputBox } from "src/components/ui/inputs";
import { ProductTable } from "src/components/tables";
import { DD_MM_YYYY } from "src/helpers";
import { toast } from "sonner";

interface BackorderPOModalProps {
  backorders: PurchaseOrder[];
  onConfirm: () => void;
  onCancel: () => void;
  onUpdateScheduledDate: (purchaseOrderId: string, scheduledDate: string) => Promise<void>;
}

const BackorderPOModal: React.FC<BackorderPOModalProps> = ({
  backorders,
  onConfirm,
  onCancel,
  onUpdateScheduledDate
}) => {
  const [scheduledDates, setScheduledDates] = useState<Record<string, string>>(() => {
    const initialDates: Record<string, string> = {};
    backorders.forEach((bo) => {
      if (bo.scheduledDate) {
        initialDates[bo.id] = bo.scheduledDate;
      }
    });
    return initialDates;
  });
  const [updatingIds, setUpdatingIds] = useState<Set<string>>(new Set());
  const [dateErrors, setDateErrors] = useState<Record<string, string>>({});

  const handleDateChange = (purchaseOrderId: string, date: string) => {
    setScheduledDates((prev) => ({ ...prev, [purchaseOrderId]: date }));
    // Clear error when user changes the date
    setDateErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[purchaseOrderId];
      return newErrors;
    });
  };

  const handleUpdateDate = async (purchaseOrderId: string) => {
    const scheduledDate = scheduledDates[purchaseOrderId];
    if (!scheduledDate) {
      setDateErrors((prev) => ({
        ...prev,
        [purchaseOrderId]: "Scheduled date is required"
      }));
      return;
    }

    setUpdatingIds((prev) => new Set(prev).add(purchaseOrderId));
    try {
      await onUpdateScheduledDate(purchaseOrderId, scheduledDate);
      toast.success(`Updated scheduled date for ${purchaseOrderId}`);
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "Failed to update scheduled date";
      setDateErrors((prev) => ({
        ...prev,
        [purchaseOrderId]: errorMessage
      }));
      toast.error(`Failed to update scheduled date: ${errorMessage}`);
    } finally {
      setUpdatingIds((prev) => {
        const newSet = new Set(prev);
        newSet.delete(purchaseOrderId);
        return newSet;
      });
    }
  };

  const handleConfirm = () => {
    // Check if all backorders have scheduled dates
    const missingDates = backorders.filter((bo) => !scheduledDates[bo.id]);
    if (missingDates.length > 0) {
      toast.warning("Please provide scheduled dates for all backorders");
      return;
    }

    // Check if there are any date errors
    if (Object.keys(dateErrors).length > 0) {
      toast.warning("Please fix date errors before continuing");
      return;
    }

    onConfirm();
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden border border-secondary-200 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-800 to-primary-900 px-6 py-5 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Package className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Backorder Purchase Orders</h2>
                <p className="text-sm text-primary-100 mt-0.5">Review and update scheduled dates for your backorders</p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="text-white/80 hover:text-white text-2xl leading-none transition-colors w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10"
            >
              &times;
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-4">
          {backorders.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-secondary-600">No backorders found</p>
            </div>
          ) : (
            backorders.map((backorder) => (
              <div
                key={backorder.id}
                className="bg-white border border-secondary-200 rounded-xl shadow-soft overflow-hidden"
              >
                <CollapsibleSection
                  initiallyOpen={false}
                  title={
                    <div className="flex items-center justify-between w-full gap-4 px-3">
                      <div className="flex items-center space-x-3">
                        <Package className="w-5 h-5 text-primary-600" />
                        <div>
                          <h3 className="text-lg font-semibold text-secondary-800">{backorder.id}</h3>
                          {backorder.originalPurchaseOrderId && (
                            <p className="text-xs text-secondary-500">
                              Original PO: {backorder.originalPurchaseOrderId}
                            </p>
                          )}
                        </div>
                      </div>
                      {backorder.scheduledDate && (
                        <div className="flex items-center space-x-2 text-sm text-secondary-600 flex-shrink-0">
                          <Calendar className="w-4 h-4" />
                          <span>Scheduled: {DD_MM_YYYY.formatDate(new Date(backorder.scheduledDate))}</span>
                        </div>
                      )}
                    </div>
                  }
                >
                  <div className="p-6 space-y-4">
                    {/* Scheduled Date Editor */}
                    <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-4">
                      <label className="block text-sm font-semibold text-secondary-900 mb-2">
                        Scheduled Date <span className="text-error-500">*</span>
                      </label>
                      <div className="flex items-start space-x-3">
                        <div className="flex-1">
                          <DateInputBox
                            id={`scheduled-date-${backorder.id}`}
                            value={scheduledDates[backorder.id] || ""}
                            onChange={(date) => handleDateChange(backorder.id, date)}
                            error={dateErrors[backorder.id]}
                            required
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleUpdateDate(backorder.id)}
                          disabled={updatingIds.has(backorder.id) || !scheduledDates[backorder.id]}
                          className="px-4 py-2 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors text-sm font-medium disabled:bg-secondary-300 disabled:text-secondary-500 disabled:cursor-not-allowed"
                        >
                          {updatingIds.has(backorder.id) ? "Updating..." : "Update Date"}
                        </button>
                      </div>
                    </div>

                    {/* Products */}
                    {backorder.products && backorder.products.length > 0 && (
                      <div>
                        <h4 className="text-sm font-semibold text-secondary-700 mb-3">Products</h4>
                        <ProductTable products={backorder.products} vendorLocation={backorder.origin} />
                      </div>
                    )}

                    {/* Additional Info */}
                    {backorder.totalCost && (
                      <div className="bg-primary-50 border border-primary-200 rounded-lg p-3">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-medium text-secondary-700">Total Cost:</span>
                          <span className="text-sm font-semibold text-secondary-900">
                            £{backorder.totalCost.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </CollapsibleSection>
              </div>
            ))
          )}
        </div>

        {/* Footer Buttons */}
        <div className="flex justify-end space-x-3 p-6 border-t border-secondary-200 bg-secondary-50 flex-shrink-0">
          <button
            className="px-5 py-2.5 bg-secondary-200 text-secondary-800 rounded-lg hover:bg-secondary-300 transition-colors text-sm font-medium"
            onClick={onCancel}
          >
            Cancel
          </button>
          <button
            className="px-5 py-2.5 bg-primary-700 text-white rounded-lg hover:bg-primary-800 transition-colors text-sm font-medium shadow-sm disabled:bg-secondary-300 disabled:text-secondary-500 disabled:cursor-not-allowed"
            onClick={handleConfirm}
            disabled={backorders.length === 0 || Object.keys(dateErrors).length > 0}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export { BackorderPOModal };
